// Week 6 — Opts mutation. Patches a subset of opts keys and persists to the
// meta doc (merge: true) so counters are preserved.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { saveOpts } from '../lib/db'
import { useUndoStore } from '../lib/undoStore'
import type { AppState } from '../types/firestore'
import type { Opts } from '../types'

export type OptsPatch = { [K in keyof Opts]?: Opts[K] | undefined }

export function useOptsMutation() {
  const qc = useQueryClient()
  const { effectiveUid: uid } = useAuth()
  const pushUndo = useUndoStore((s) => s.push)

  function getState(): AppState | undefined {
    return qc.getQueryData<AppState>(qk.appstate(uid!))
  }

  return useMutation({
    mutationFn: async (patch: OptsPatch): Promise<void> => {
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      pushUndo(state)
      const nextOpts: Opts = { ...state.opts }
      for (const k of Object.keys(patch)) {
        const v = (patch as Record<string, unknown>)[k]
        if (v === undefined) delete (nextOpts as Record<string, unknown>)[k]
        else                 (nextOpts as Record<string, unknown>)[k] = v
      }
      qc.setQueryData(qk.appstate(uid), { ...state, opts: nextOpts })
      await saveOpts(uid, nextOpts)
    },
    onError: () => void qc.invalidateQueries({ queryKey: qk.appstate(uid!) }),
  })
}
